import * as THREE from 'three';

/**
 * 周期角参数域定义。
 */
export interface PeriodicIntersectionDomain2D {
    /** 起始角。 */
    startAngle: number;
    /** 终止角。 */
    endAngle: number;
    /** 是否按顺时针方向增长参数。 */
    clockwise: boolean;
    /** 是否覆盖完整周期。 */
    isFull: boolean;
}

/**
 * 周期参数曲线求解器抽象基类。
 *
 * 职责：
 * 1. 统一角度归一化和周期接缝语义；
 * 2. 按顺/逆时针方向计算从起始角到候选角的有向偏移；
 * 3. 将支撑曲线角参数过滤并映射到局部 `[0, 1]` 参数域。
 */
export abstract class AbstractPeriodicIntersectionSolver {
    /** 单圈弧度。 */
    protected static readonly FULL_TURN = Math.PI * 2;

    /** 周期参数域判定容差。 */
    private static readonly DOMAIN_TOLERANCE = 1e-10;

    /**
     * 将角度归一化到 `[0, 2π)`。
     *
     * @param angle 原始角度。
     * @returns 归一化角度。
     */
    protected normalizeAngle(angle: number): number {
        const fullTurn = AbstractPeriodicIntersectionSolver.FULL_TURN;
        const normalized = ((angle % fullTurn) + fullTurn) % fullTurn;
        return normalized >= fullTurn - AbstractPeriodicIntersectionSolver.DOMAIN_TOLERANCE
            ? 0
            : normalized;
    }

    /**
     * 将支撑曲线角参数投影到指定周期域。
     *
     * @param angle 支撑曲线角参数。
     * @param domain 目标周期参数域。
     * @returns 局部归一化参数；不在域内时返回 null。
     */
    protected projectAngleToDomainParameter(
        angle: number,
        domain: PeriodicIntersectionDomain2D,
    ): number | null {
        const normalizedAngle = this.normalizeAngle(angle);
        if (domain.isFull) {
            return normalizedAngle / AbstractPeriodicIntersectionSolver.FULL_TURN;
        }

        const normalizedStart = this.normalizeAngle(domain.startAngle);
        const normalizedEnd = this.normalizeAngle(domain.endAngle);
        const sweep = domain.clockwise
            ? this.normalizeAngle(normalizedStart - normalizedEnd)
            : this.normalizeAngle(normalizedEnd - normalizedStart);
        if (sweep <= AbstractPeriodicIntersectionSolver.DOMAIN_TOLERANCE) {
            return null;
        }

        const offset = domain.clockwise
            ? this.normalizeAngle(normalizedStart - normalizedAngle)
            : this.normalizeAngle(normalizedAngle - normalizedStart);
        if (offset > sweep + AbstractPeriodicIntersectionSolver.DOMAIN_TOLERANCE) {
            return null;
        }

        return THREE.MathUtils.clamp(offset / sweep, 0, 1);
    }
}
